import {Classes, InputGroup, Menu, MenuDivider, MenuItem, Popover, Tag, Text} from "@blueprintjs/core";
import {ItemRenderer, Select} from "@blueprintjs/select";
import styled from "@emotion/styled";
import {isEqual} from "lodash";
import {SyntheticEvent, useCallback, useContext, useEffect, useState} from "react";
import {PreferenceContext, extractJsonPref} from "../../../providers/PreferenceProvider";
import {Constants, DEFAULT_POPOVER_MODIFIERS, UserPreferences} from "../../../constants/constants";
import {OKButton} from "../../manifest/details/components/TimePickerCommon";
import "./TimeQuickFilterSelect.css";
import {addHours, addMinutes, endOfMinute, startOfMinute, subHours, subMinutes} from "date-fns";
import {StyledSelectButton} from "../StyledButtons";

enum TimeFilterType {
  ready = "ready",
  due = "due",
  placedInLast = "placedInLast"
}
export interface TimeOption {
  type?: TimeFilterType;
  title: string;
  isClearFilter?: boolean;
  getFilterValue(customValue?: number): any;
  customValue?: number;
}

interface TimeFilterSelectProps {
  selectedOption: TimeOption | undefined;
  onConfirm?: (option: TimeOption) => void;
  onOptionSelected(item: TimeOption, event?: SyntheticEvent<HTMLElement, Event> | undefined): void;
  onClear(): void;
}

interface TimeFilterMenuItemsProps {
  titleDivider: string;
  options: TimeOption[];
  selectingOption: TimeOption | undefined;
  customInputValue: number;
  onSelectOption: React.Dispatch<React.SetStateAction<TimeOption | undefined>>;
  onChangeCustomInput: (value: number | string) => void;
}

export type TTimeFilterCustomValue = {
  ready: number;
  due: number;
  placedInLast?: number;
};

export const DefaultTimeFilterCustomValue: TTimeFilterCustomValue = {
  ready: 0,
  due: 0,
  placedInLast: 0
};

const ReadyDueFilterType = [TimeFilterType.ready, TimeFilterType.due];

const ReadyDueTimeOptionData: TimeOption[] = [
  {
    title: "Now",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(new Date())];
    }
  },
  {
    title: "30min",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 30))];
    }
  },
  {
    title: "1hr",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(addHours(new Date(), 1))];
    }
  },
  {
    title: "1hr 30min",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 90))];
    }
  },
  {
    title: "2hr",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(addHours(new Date(), 2))];
    }
  },
  {
    title: "2hr 30min",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 150))];
    }
  },
  {
    title: "3hr",
    getFilterValue(): any {
      return [startOfMinute(new Date(0)), endOfMinute(addHours(new Date(), 3))];
    }
  },
  {
    title: "custom",
    customValue: 0,
    getFilterValue(customValue: number): any {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), customValue * 60))];
    }
  }
];

export const ReadyDueFilterOptions: TimeOption[] = ReadyDueFilterType.flatMap((type) =>
  ReadyDueTimeOptionData.map((option) => ({...option, type}))
);

const PlacedInLastFilterOptionData = [
  {
    title: "30min",
    getFilterValue(): any {
      return [startOfMinute(subMinutes(new Date(), 30)), endOfMinute(new Date())];
    }
  },
  {
    title: "1hr",
    getFilterValue(): any {
      return [startOfMinute(subHours(new Date(), 1)), endOfMinute(new Date())];
    }
  },
  {
    title: "1hr 30min",
    getFilterValue(): any {
      return [startOfMinute(subMinutes(new Date(), 90)), endOfMinute(new Date())];
    }
  },
  {
    title: "2hr",
    getFilterValue(): any {
      return [startOfMinute(subHours(new Date(), 2)), endOfMinute(new Date())];
    }
  },
  {
    title: "2hr 30min",
    getFilterValue(): any {
      return [startOfMinute(subMinutes(new Date(), 150)), endOfMinute(new Date())];
    }
  },
  {
    title: "3hr",
    getFilterValue(): any {
      return [startOfMinute(subHours(new Date(), 3)), endOfMinute(new Date())];
    }
  },
  {
    title: "Clear Filter",
    isClearFilter: true,
    getFilterValue(): any {
      return [];
    }
  }
];

export const PlacedInLastFilterOptions: TimeOption[] = PlacedInLastFilterOptionData.map((option) => ({
  ...option,
  type: TimeFilterType.placedInLast
}));

const getTimeFilterDisplayValue = (selectedOption: TimeOption) => {
  const suffix = isCustomOption(selectedOption) ? selectedOption.customValue?.toString() + "hr" : selectedOption.title;
  let prefix = "";
  switch (selectedOption.type) {
    case TimeFilterType.ready:
      prefix = "Ready in Next";
      break;
    case TimeFilterType.due:
      prefix = "Due in Next";
      break;
    case TimeFilterType.placedInLast:
      prefix = "Placed in Last";
      break;
    default:
      break;
  }
  return `${prefix} ${suffix}`;
};

export const isCustomOption = (option: TimeOption) => option.title === "custom";

export const getTimeOptionFilterModel = (option?: TimeOption): any => {
  return option
    ? {
        filterType: "date",
        type: "inRange",
        option: option,
        value: getTimeFilterDisplayValue(option)
      }
    : {};
};

const TimeFilterMenuItems = ({
  titleDivider,
  options,
  selectingOption,
  customInputValue,
  onChangeCustomInput,
  onSelectOption
}: TimeFilterMenuItemsProps) => {
  const isMenuItemActive = useCallback(
    (option: TimeOption) => {
      if (!selectingOption) return false;
      return selectingOption.title === option.title && selectingOption.type === option.type;
    },
    [selectingOption]
  );

  return (
    <>
      <MenuDivider data-testid="time-menu-divider" title={<TextGray>{titleDivider}</TextGray>} />
      {options.map((option) => {
        if (isCustomOption(option)) {
          return (
            <MenuItem
              key={option.title}
              data-testid={`${option.title}-selection`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectOption({...option, customValue: customInputValue});
              }}
              selected={isMenuItemActive(option)}
              icon={isMenuItemActive(option) ? "tick" : "blank"}
              text={
                <CustomTimeFilterContainer>
                  <TextGray>In Next</TextGray>
                  <StyledInput
                    data-testid="custom-time-input"
                    title=""
                    type="number"
                    min={0}
                    max={99}
                    step={0.1}
                    value={customInputValue.toString()}
                    rightElement={<TimeTag style={{color: "#161616"}}>hr</TimeTag>}
                    onBlur={(e) => {
                      onChangeCustomInput(parseFloat(Number(e.target.value).toFixed(2)));
                    }}
                    onChange={(e) => {
                      if (Number(e.target.value) <= Constants.MAX_HOUR_JOBS_FILTER) {
                        onChangeCustomInput(e.target.value.trim().replaceAll("-", ""));
                      }
                    }}
                  />
                </CustomTimeFilterContainer>
              }
            />
          );
        }

        return (
          <MenuItem
            data-testid={`${option.title}-selection`}
            key={option.title}
            onClick={(e) => {
              e.stopPropagation();
              onSelectOption(option);
            }}
            selected={isMenuItemActive(option)}
            icon={isMenuItemActive(option) ? "tick" : "blank"}
            text={
              <MenuItemContainer>
                {option.title === "Now" ? (
                  <TextBlack>{option.title}</TextBlack>
                ) : (
                  <>
                    <TextGray>In Next</TextGray> <TextBlack>{option.title}</TextBlack>
                  </>
                )}
              </MenuItemContainer>
            }
          />
        );
      })}
    </>
  );
};

export const ReadyDueTimeFilterSelect = ({selectedOption, onConfirm, onClear}: TimeFilterSelectProps) => {
  const {userPreferences} = useContext(PreferenceContext);
  const timeFilterCustomValue = extractJsonPref(
    userPreferences,
    UserPreferences.jobGridFilterReadyDueCustomValue,
    DefaultTimeFilterCustomValue
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectingOption, setSelectingOption] = useState<TimeOption | undefined>(selectedOption);
  const [customValue, setCustomValue] = useState<TTimeFilterCustomValue>({
    ...DefaultTimeFilterCustomValue,
    ...timeFilterCustomValue.value
  });

  const onChangeCustomInput = useCallback((value, type: TimeFilterType) => {
    setCustomValue((prev) => {
      return {...prev, [type]: value};
    });
  }, []);

  const renderTitle = useCallback(() => {
    if (!selectedOption) {
      return "Ready / Due";
    }
    const title = getTimeFilterDisplayValue(selectedOption);
    return title.includes("Now") ? title.replace("in Next", "") : title;
  }, [selectedOption]);

  const onConfirmFilter = useCallback(() => {
    if (!isEqual(selectingOption, selectedOption)) {
      onConfirm?.(selectingOption!);
      setIsOpen(false);
    }
  }, [onConfirm, selectedOption, selectingOption]);

  const onClearFilter = useCallback(() => {
    onClear();
    setIsOpen(false);
  }, [onClear]);

  const onPressEnter = useCallback(
    (e) => {
      if (e.key === "Enter" || e.keyCode === 13) {
        if ((customValue.ready as any) === "" || (customValue.due as any) === "") {
          e.stopPropagation();
          return;
        }
        onConfirmFilter();
      }
    },
    [customValue.due, customValue.ready, onConfirmFilter]
  );

  useEffect(() => {
    if (selectingOption && isCustomOption(selectingOption)) {
      const customOption: TimeOption = {
        ...selectingOption,
        customValue: customValue[selectingOption.type!]
      };
      setSelectingOption(customOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customValue]);

  useEffect(() => {
    if (!isOpen) {
      if (selectedOption && isCustomOption(selectedOption)) {
        setCustomValue({
          ...DefaultTimeFilterCustomValue,
          ...timeFilterCustomValue.value,
          [selectedOption?.type as string]: selectedOption?.customValue
        });
      } else {
        setCustomValue({
          ...DefaultTimeFilterCustomValue,
          ...timeFilterCustomValue.value
        });
      }
      setSelectingOption(selectedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedOption]);

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      canEscapeKeyClose
      content={
        <TimePickerContainer onKeyDown={onPressEnter}>
          <Body>
            <TimePickerMenuCustom>
              {ReadyDueFilterType.map((title) => (
                <TimeFilterMenuItems
                  key={title}
                  titleDivider={title}
                  options={ReadyDueFilterOptions.filter((o) => o.type === title)}
                  selectingOption={selectingOption}
                  onSelectOption={setSelectingOption}
                  customInputValue={customValue[title]!}
                  onChangeCustomInput={(value) => onChangeCustomInput(value, title)}
                />
              ))}
            </TimePickerMenuCustom>
          </Body>
          <Footer>
            <OKButton
              data-testid="confirm-filter-time"
              style={{fontSize: "14px"}}
              active={!isEqual(selectingOption, selectedOption)}
              onClick={onConfirmFilter}
            >
              OK
            </OKButton>
            <TextClearFilter
              data-testid="clear-filter-ready-due"
              className={Classes.POPOVER_DISMISS}
              onClick={onClearFilter}
            >
              Clear Filter
            </TextClearFilter>
          </Footer>
        </TimePickerContainer>
      }
      placement="bottom-start"
      popoverClassName="time-picker-popover"
      minimal
    >
      <StyledSelectButton
        data-testid={"ready-due-filter-btn"}
        alignText="left"
        text={renderTitle()}
        endIcon="caret-down"
        hasValue={Boolean(selectedOption)}
        onClick={() => setIsOpen((prev) => !prev)}
      />
    </Popover>
  );
};

export const PlacedInLastFilterSelect = ({selectedOption, onOptionSelected, onClear}: TimeFilterSelectProps) => {
  const renderFilm: ItemRenderer<TimeOption> = (option, {handleClick, handleFocus, modifiers}) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    if (option.isClearFilter) {
      return (
        <Footer>
          <TextClearFilter
            data-testid="clear-filter-placed-in-last"
            className={Classes.POPOVER_DISMISS}
            onClick={onClear}
          >
            Clear Filter
          </TextClearFilter>
        </Footer>
      );
    }
    return (
      <MenuItem
        data-testid={`time-select-${option.title}`}
        active={selectedOption?.title === option.title}
        disabled={modifiers.disabled}
        icon={selectedOption?.title === option.title ? "tick" : "blank"}
        key={option.title}
        onClick={handleClick}
        onFocus={handleFocus}
        roleStructure="listoption"
        text={option.title}
      />
    );
  };

  return (
    <Select<TimeOption>
      popoverProps={{
        popoverClassName: "time-quick-filter-select",
        minimal: true,
        modifiers: DEFAULT_POPOVER_MODIFIERS
      }}
      items={PlacedInLastFilterOptions}
      filterable={false}
      itemRenderer={renderFilm}
      onItemSelect={onOptionSelected}
    >
      <StyledSelectButton
        data-testid={"placed-in-last-filter-btn"}
        text={selectedOption ? getTimeFilterDisplayValue(selectedOption) : "Placed in Last"}
        hasValue={!!selectedOption}
        endIcon="caret-down"
      />
    </Select>
  );
};

const TextGray = styled(Text)`
  text-transform: capitalize;
  font-size: 14px;
  color: rgb(106, 106, 106);
`;

const TextBlack = styled(Text)`
  font-size: 14px;
  color: black;
`;

const MenuItemContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
`;

const TimePickerMenuCustom = styled(Menu)`
  &.bp5-menu {
    max-height: 410px;
    overflow-y: scroll;
  }

  &.bp5-menu-item {
    padding-left: 15px;
  }
  &.bp5-menu.bp5-menu-header {
    font-weight: 100;
  }
`;

const TimePickerContainer = styled.div``;

const Body = styled.div`
  padding: 4px 3px 0;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  background: #fcfcfc;
  box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.15);
  padding: 10px;
`;

export const TextClearFilter = styled(Text)`
  color: #14305a;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  cursor: pointer;
  text-align: right;
`;

const CustomTimeFilterContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledInput = styled(InputGroup)`
  display: flex;
  align-items: center;

  .bp5-input {
    width: 60px;
    height: 20px;
    border-radius: 4px;
    border: 1px solid #c3c3c373;
    box-shadow: none;
    padding: 4px;

    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const TimeTag = styled(Tag)`
  background: none !important;
  margin: 0 !important;
  padding: 0 2px !important;
`;
